// frontend/src/components/CsvViewer.tsx
// Changes:
// - Integrated getDisplayFileName from FileUtils.ts for ProcessLog files
// - Ensured backward compatibility by using getFileName for downloads
// - Added debug logging in handleDownload, handleExportFiltered, and for decodedKey/displayFileName
// - Updated tooltips and labels to indicate ProcessLog files
// - Added fallback message if accession ID can't be extracted
// - Modified "Back to List" button to use navigate(-1) instead of hardcoded route to "/"
// - Preserved all original logic

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom'; // Add useNavigate
import { csvParse, csvFormat } from 'd3-dsv';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Breadcrumbs,
  IconButton,
  Tooltip,
  TextField,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Home as HomeIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  TableChart as TableIcon,
  Info as InfoIcon,
  Sort as SortIcon
} from '@mui/icons-material';
import { 
  fetchCsvContent, 
  getPresignedDownloadUrl, 
  getFileMetadata,
  getFileName,
  apiLogger
} from '../services/api';
import { getDisplayFileName } from '../services/FileUtils';

const COMPONENT_CONTEXT = 'CsvViewer';
const MAX_SAFE_ROWS = 10000;
const INITIAL_VISIBLE_ROWS = 200;
const CHUNK_SIZE = 1000;
const DISPLAY_ALL = 'all';

const CsvViewer: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate(); // Add useNavigate hook
  const decodedKey = key ? decodeURIComponent(key) : '';
  console.log('[Debug] decodedKey:', decodedKey);
  const originalFileName = getFileName(decodedKey);
  const displayFileName = getDisplayFileName(decodedKey);
  console.log('[Debug] displayFileName:', displayFileName);
  
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [progressiveLoading, setProgressiveLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filterValue, setFilterValue] = useState<string>('');
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [metadata, setMetadata] = useState<any>(null);
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [visibleRowsLimit, setVisibleRowsLimit] = useState<number | string>(INITIAL_VISIBLE_ROWS);
  const [isLargeFile, setIsLargeFile] = useState<boolean>(false);
  const [sortColumn, setSortColumn] = useState<number | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [originalColumnCount, setOriginalColumnCount] = useState<number>(0);

  useEffect(() => {
    apiLogger.info(COMPONENT_CONTEXT, `Mounted CSV viewer for file: ${decodedKey}`);
    return () => {
      apiLogger.info(COMPONENT_CONTEXT, `Unmounted CSV viewer for file: ${decodedKey}`);
    };
  }, [decodedKey]);

  const fetchMetadata = useCallback(async () => {
    if (!decodedKey) return;
    try {
      setLoadingMetadata(true);
      const result = await getFileMetadata(decodedKey);
      setMetadata(result);
    } catch (err) {
      apiLogger.warn(COMPONENT_CONTEXT, `Failed to load metadata for ${decodedKey}`, err);
    } finally {
      setLoadingMetadata(false);
    }
  }, [decodedKey]);

  const normalizeCellValue = (value: any): string => {
    const stringValue = String(value).trim();
    if (
      stringValue === "" ||
      stringValue === "null" ||
      stringValue === "undefined" ||
      stringValue === '""' ||
      stringValue === '"' ||
      stringValue === "''" ||
      stringValue === "'" ||
      stringValue === 'N/A' ||
      /^["']+$/.test(stringValue)
    ) {
      return "N/A";
    }
    return stringValue;
  };

  const parseCsvChunk = useCallback((data: string, startIndex: number, chunkSize: number): string[][] => {
    try {
      const parsed = csvParse(data, (row) => {
        const rowArray = Object.values(row);
        return rowArray.map(cell => normalizeCellValue(cell));
      });
  
      const rows: string[][] = [];
      if (parsed.columns && parsed.columns.length > 0) {
        rows.push(parsed.columns.map(column => String(column)));
      }
  
      parsed.forEach(row => {
        const rowArray = Object.values(row).map(value => String(value));
        rows.push(rowArray);
      });
  
      if (startIndex === 0 && rows.length > 0) {
        setOriginalColumnCount(rows[0].length);
      }
  
      return rows.slice(startIndex, startIndex + chunkSize);
    } catch (error) {
      apiLogger.error(COMPONENT_CONTEXT, `Error parsing CSV chunk with d3-dsv: ${error}`);
      return [];
    }
  }, []);

  useEffect(() => {
    async function loadCsvData() {
      if (!decodedKey) {
        setError('No file specified');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        apiLogger.info(COMPONENT_CONTEXT, `Fetching CSV content for: ${decodedKey}`);
        const response = await fetchCsvContent(decodedKey);
        const rawData = response.data;

        if (!rawData) {
          setError('No CSV content received');
          setLoading(false);
          return;
        }

        const initialRows = parseCsvChunk(rawData, 0, INITIAL_VISIBLE_ROWS);
        setCsvData(initialRows);
        setError(null);
        fetchMetadata();

        const totalRows = rawData.split('\n').length - 1;
        if (totalRows > INITIAL_VISIBLE_ROWS) {
          setProgressiveLoading(true);
          setIsLargeFile(totalRows > MAX_SAFE_ROWS);
          apiLogger.info(COMPONENT_CONTEXT, `Large CSV detected (${totalRows} rows), loading progressively`);

          const loadRemaining = async () => {
            let currentIndex = INITIAL_VISIBLE_ROWS;
            while (currentIndex < totalRows) {
              const nextChunk = parseCsvChunk(rawData, currentIndex, CHUNK_SIZE);
              setCsvData(prev => [...prev, ...nextChunk]);
              currentIndex += CHUNK_SIZE;
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            setProgressiveLoading(false);
            apiLogger.info(COMPONENT_CONTEXT, `Completed progressive load with ${totalRows} rows`);
          };
          loadRemaining();
        } else {
          apiLogger.info(COMPONENT_CONTEXT, `Loaded small CSV with ${initialRows.length} rows`);
        }
      } catch (err) {
        apiLogger.error(COMPONENT_CONTEXT, `Error fetching CSV data: ${err}`, err);
        setError(`Failed to load CSV data: ${err instanceof Error ? err.message : String(err)}`);
      } finally {
        setLoading(false);
      }
    }
    loadCsvData();
  }, [decodedKey, fetchMetadata, parseCsvChunk]);

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      apiLogger.info(COMPONENT_CONTEXT, `Downloading original file: ${decodedKey}, Display name: ${displayFileName}`);
      const response = await getPresignedDownloadUrl(decodedKey);
      const link = document.createElement('a');
      link.href = response.url;
      link.download = response.filename || originalFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Error downloading CSV file: ${err}`, { decodedKey, error: err });
      setError(`Failed to download file: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleExportFiltered = async () => {
    try {
      setExportLoading(true);
      apiLogger.info(COMPONENT_CONTEXT, `Exporting filtered CSV data for: ${decodedKey}, Display name: ${displayFileName}`);
      const filteredData = getFilteredData();
  
      const headers = filteredData[0].map(header => String(header));
      const rows = filteredData.slice(1).map(row => {
        const rowObj: { [key: string]: string } = {};
        headers.forEach((header, index) => {
          rowObj[header] = row[index];
        });
        return rowObj;
      });
  
      const csvContent = csvFormat(rows);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `filtered_${displayFileName}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      apiLogger.error(COMPONENT_CONTEXT, `Error exporting filtered CSV: ${err}`, { decodedKey, error: err });
      setError(`Failed to export filtered data: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1); // Navigate back to the previous page in history
  };

  const getFilteredData = useCallback(() => {
    if (!filterValue.trim() || csvData.length === 0) return csvData;
    const searchTerm = filterValue.toLowerCase();
    return csvData.filter((row, rowIndex) => {
      if (rowIndex === 0) return true;
      return row.some(cell => String(cell).toLowerCase().includes(searchTerm));
    });
  }, [csvData, filterValue]);

  const getSortedData = useCallback((data: string[][]) => {
    if (sortColumn === null || data.length <= 1) return data;
    const header = data[0];
    const rows = data.slice(1).sort((a, b) => {
      const aValue = a[sortColumn] || '';
      const bValue = b[sortColumn] || '';
      return sortDirection === 'asc' 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    });
    return [header, ...rows];
  }, [sortColumn, sortDirection]);

  const getVisibleData = useCallback(() => {
    const filteredData = getFilteredData();
    const sortedData = getSortedData(filteredData);
    if (sortedData.length <= 1) return sortedData;
    const header = sortedData[0];
    const dataRows = sortedData.slice(1);
    
    if (visibleRowsLimit === DISPLAY_ALL) {
      return [header, ...dataRows];
    }
    
    const limitedRows = dataRows.slice(0, visibleRowsLimit as number);
    return [header, ...limitedRows];
  }, [getFilteredData, getSortedData, visibleRowsLimit]);

  const handleRowLimitChange = (event: SelectChangeEvent<number | string>) => {
    setVisibleRowsLimit(event.target.value === DISPLAY_ALL ? DISPLAY_ALL : Number(event.target.value));
  };

  const handleSort = (columnIndex: number) => {
    if (sortColumn === columnIndex) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnIndex);
      setSortDirection('asc');
    }
    apiLogger.info(COMPONENT_CONTEXT, `Sorting by column ${columnIndex}, direction: ${sortDirection === 'asc' ? 'desc' : 'asc'}`);
  };

  const filteredData = getFilteredData();
  const visibleData = getVisibleData();
  const totalRows = csvData.length > 0 ? csvData.length - 1 : 0;
  const filteredRows = filteredData.length > 0 ? filteredData.length - 1 : 0;
  const isLimited = visibleRowsLimit !== DISPLAY_ALL && filteredRows > (visibleRowsLimit as number);

  const renderMetadata = () => {
    if (!metadata || loadingMetadata) return null;
    return (
      <Box sx={{ mt: 2, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          File Information
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {metadata.mimeType && (
            <Chip size="small" label={`Type: ${metadata.mimeType}`} variant="outlined" />
          )}
          {metadata.contentLength && (
            <Chip size="small" label={`Size: ${(metadata.contentLength / (1024 * 1024)).toFixed(2)} MB`} variant="outlined" />
          )}
          {metadata.lastModified && (
            <Chip size="small" label={`Modified: ${new Date(metadata.lastModified).toLocaleDateString()}`} variant="outlined" />
          )}
          <Chip size="small" label={`Rows: ${totalRows}`} variant="outlined" color="primary" />
          {csvData.length > 0 && csvData[0].length > 0 && (
            <Chip size="small" label={`Columns: ${csvData[0].length}`} variant="outlined" color="primary" />
          )}
        </Box>
      </Box>
    );
  };

  const getColumnWidth = (columnIndex: number) => {
    if (originalColumnCount > 0 && columnIndex === originalColumnCount - 1) {
      return '400px';
    }
    
    if (columnIndex === 0) return '100px';
    if (columnIndex === 1) return '110px';
    if (columnIndex === 2) return '140px';
    if (columnIndex === 3) return '200px';
    if (columnIndex === 4) return '350px';
    if (columnIndex === 5) return '350px';
    return '150px';
  };

  return (
    <Box sx={{ maxWidth: '100%', overflowX: 'hidden', p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}>
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Typography color="text.primary">{displayFileName}</Typography>
      </Breadcrumbs>
      
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" component="h1">{displayFileName}</Typography>
            <Chip 
              icon={<TableIcon />} 
              label={decodedKey.includes('ProcessLog.csv') ? 'Process Log' : 'CSV'} 
              color="primary" 
              variant="outlined" 
              size="small" 
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Filter data">
              <IconButton color="primary" onClick={() => setShowFilter(!showFilter)} size="small" aria-label="Toggle filter">
                {showFilter ? <CloseIcon /> : <FilterIcon />}
              </IconButton>
            </Tooltip>
            <Button 
              variant="contained" 
              startIcon={downloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleDownload}
              size="small"
              disabled={downloadLoading || exportLoading}
              aria-label={`Download original ${displayFileName}${decodedKey.includes('ProcessLog.csv') ? ' (Process Log)' : ''}`}
            >
              Download
            </Button>
            <Button 
              variant="outlined" 
              startIcon={exportLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExportFiltered}
              size="small"
              disabled={downloadLoading || exportLoading || filteredRows === 0}
              aria-label={`Export filtered ${displayFileName}${decodedKey.includes('ProcessLog.csv') ? ' (Process Log)' : ''}`}
            >
              Export Filtered
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={handleBack} // Use the handler instead of component={Link} to="/"
              size="small"
              aria-label="Back to previous page"
            >
              Back to List
            </Button>
            {metadata && (
              <Tooltip title="View file metadata">
                <IconButton color="primary" onClick={() => alert(JSON.stringify(metadata, null, 2))} aria-label="View file metadata">
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        
        {displayFileName === originalFileName && decodedKey.includes('ProcessLog.csv') && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not extract accession ID from file path. Displaying original file name.
          </Alert>
        )}
        
        {renderMetadata()}
        
        {showFilter && (
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Filter all rows..."
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              InputProps={{
                endAdornment: filterValue ? (
                  <IconButton size="small" onClick={() => setFilterValue('')} edge="end" aria-label="Clear filter">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
              aria-label="Filter CSV data"
            />
          </Box>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : csvData.length === 0 ? (
          <Alert severity="info">This CSV file appears to be empty.</Alert>
        ) : (
          <>
            {isLargeFile && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This is a large file ({totalRows} rows). {progressiveLoading ? 'Loading remaining rows...' : 'Showing only the first rows below.'} Download for full data.
              </Alert>
            )}
            
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {filterValue 
                  ? `Showing ${isLimited ? visibleRowsLimit : filteredRows} of ${filteredRows} filtered rows (searched across all ${totalRows} rows)`
                  : `Showing ${isLimited ? visibleRowsLimit : totalRows} of ${totalRows} rows`}
                {progressiveLoading && ' (loading more...)'}
              </Typography>
              
              {totalRows > 100 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl variant="outlined" size="small">
                    <InputLabel id="visible-rows-label">Display rows</InputLabel>
                    <Select
                      labelId="visible-rows-label"
                      value={visibleRowsLimit}
                      onChange={handleRowLimitChange}
                      label="Display rows"
                      sx={{ minWidth: 120 }}
                    >
                      <MenuItem value={100}>First 100</MenuItem>
                      <MenuItem value={200}>First 200</MenuItem>
                      <MenuItem value={500}>First 500</MenuItem>
                      <MenuItem value={1000}>First 1000</MenuItem>
                      <MenuItem value={DISPLAY_ALL}>All Rows</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
            
            <TableContainer 
              sx={{ 
                maxHeight: '60vh', 
                overflow: 'auto', 
                '& .MuiTable-root': {
                  tableLayout: 'fixed',
                  width: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                },
                '& .MuiTableCell-root': {
                  borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                }
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {visibleData[0]?.map((header, index) => (
                      <TableCell 
                        key={`header-${index}`} 
                        sx={{ 
                          fontWeight: 'bold', 
                          backgroundColor: 'primary.main', 
                          color: 'primary.contrastText',
                          cursor: 'pointer',
                          width: getColumnWidth(index),
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          padding: '8px 16px',
                        }}
                        onClick={() => handleSort(index)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          {header || `Column ${index + 1}`}
                          <SortIcon fontSize="small" sx={{ opacity: sortColumn === index ? 1 : 0.5 }} />
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleData.slice(1).map((row, rowIndex) => (
                    <TableRow 
                      key={`row-${rowIndex}`} 
                      hover
                      sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.03)' } }}
                    >
                      {row.map((cell, cellIndex) => (
                        <TableCell 
                          key={`cell-${rowIndex}-${cellIndex}`}
                          sx={{
                            width: getColumnWidth(cellIndex),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: cellIndex === 4 ? 'normal' : 'nowrap',
                            padding: '8px 16px',
                            minWidth: '50px',
                          }}
                        >
                          <Tooltip title={cell === "N/A" ? "Empty value" : cell}>
                            <div>
                              {cell === "N/A" ? (
                                <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
                              ) : (
                                cell
                              )}
                            </div>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default CsvViewer;